/**
 * Mission Control Theme
 *
 * Design tokens for the Order Detail view, read off the current ground via
 * useMissionTheme().
 *
 * Usage:
 *   const { mc, pl, sla, glass, badges } = useMissionTheme();
 */

import { useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { useThemeMode } from '../../context/ThemeModeContext';
import { DEFAULT_GROUND, GroundTokens, defaultGroundForMode, getGround } from '../../theme/grounds';

// ============================================================================
// Types
// ============================================================================

export type MissionColorTokens = ReturnType<typeof missionColorsForGround>;
export type PowerLineTokens = ReturnType<typeof powerLineColorsForGround>;
export type SlaColorSet = { bg: string; border: string; text: string };
export type SlaTokens = Record<'safe' | 'watch' | 'warning' | 'critical', SlaColorSet>;
export type IntelBadge = { icon: string; color: string; label: string; threshold?: number };

// ============================================================================
// Palette — a view over the ground (see ../../theme/grounds.ts)
// ============================================================================

/**
 * Mission Control's token names predate the grounds and are kept; the values
 * come from the ground. `secondary`/`info` (the old cyan) is the neutral info
 * blue, `accent` the ground's crit tone, and the power line runs ok/info/crit.
 */
export function missionColorsForGround(g: GroundTokens) {
  const dark = g.mode === 'dark';
  const glow = dark ? 0.3 : 0.12;
  const info = dark ? '#5FA8FF' : '#1D6FD1';
  const gold = dark ? '#F3C64A' : '#8A6A0A';
  return {
    // Backgrounds
    background: g.canvas,
    elevated: g.panel,
    surface: g.raised,

    // Glass
    glass: alpha(g.panel, 0.86),
    glassBorder: g.lineSoft,
    glassHighlight: alpha(g.tx, dark ? 0.04 : 0.02),

    // Accents
    accent: g.crit,
    accentGlow: alpha(g.crit, glow),
    secondary: info,
    secondaryGlow: alpha(info, glow),

    // Status Colors
    success: g.ok,
    successGlow: alpha(g.ok, glow),
    warning: g.warn,
    warningGlow: alpha(g.warn, glow),
    error: g.crit,
    info,

    // Text
    textPrimary: g.tx,
    textSecondary: g.tx2,
    textMuted: g.tx3,

    // Special
    gold,
    goldGlow: alpha(gold, glow),

    // Borders
    border: g.line,
    borderActive: alpha(info, 0.5),

    // Hover states
    hover: g.hover,
  };
}

export function powerLineColorsForGround(g: GroundTokens) {
  const dark = g.mode === 'dark';
  const info = dark ? '#5FA8FF' : '#1D6FD1';
  return {
    inactive: g.track,
    active: info,
    completed: g.ok,
    current: g.crit,
    glow: alpha(info, dark ? 0.5 : 0.25),
  };
}

export function slaColorsForGround(g: GroundTokens): SlaTokens {
  const dark = g.mode === 'dark';
  const info = dark ? '#5FA8FF' : '#1D6FD1';
  const bg = dark ? 0.1 : 0.08;
  return {
    safe: { bg: alpha(g.ok, bg), border: g.ok, text: g.ok },
    watch: { bg: alpha(info, bg), border: info, text: info },
    warning: { bg: alpha(g.warn, bg), border: g.warn, text: g.warn },
    critical: { bg: alpha(g.crit, bg), border: g.crit, text: g.crit },
  };
}

const darkMissionColors = missionColorsForGround(getGround(DEFAULT_GROUND));
const lightMissionColors: typeof darkMissionColors = missionColorsForGround(getGround(defaultGroundForMode('light')));
const darkPowerLineColors = powerLineColorsForGround(getGround(DEFAULT_GROUND));
const lightPowerLineColors: typeof darkPowerLineColors = powerLineColorsForGround(getGround(defaultGroundForMode('light')));
const darkSlaColors: SlaTokens = slaColorsForGround(getGround(DEFAULT_GROUND));
const lightSlaColors: SlaTokens = slaColorsForGround(getGround(defaultGroundForMode('light')));

// ============================================================================
// Mode Selector (the default ground of that mode) — prefer useMissionTheme()
// ============================================================================

export function getMissionColors(mode: 'light' | 'dark') {
  return mode === 'light' ? lightMissionColors : darkMissionColors;
}

export function getPowerLineColors(mode: 'light' | 'dark') {
  return mode === 'light' ? lightPowerLineColors : darkPowerLineColors;
}

export function getSlaColors(mode: 'light' | 'dark') {
  return mode === 'light' ? lightSlaColors : darkSlaColors;
}

function buildIntelBadges(mc: MissionColorTokens) {
  return {
    highValue: { icon: '💰', color: mc.gold, label: 'High Value', threshold: 500 },
    generousTipper: { icon: '⭐', color: mc.warning, label: 'Generous Tipper', threshold: 0.1 },
    loyaltyMember: { icon: '🏆', color: mc.success, label: 'Loyalty Member' },
    repeatCustomer: { icon: '🔁', color: mc.secondary, label: 'Repeat Customer', threshold: 2 },
    firstOrder: { icon: '🆕', color: mc.info, label: 'First Order' },
  };
}

function buildGlassStyle(mc: MissionColorTokens) {
  return {
    background: mc.glass,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: `1px solid ${mc.glassBorder}`,
    borderRadius: 3,
  };
}

// ============================================================================
// Hook: useMissionTheme() — mode-aware (preferred)
// ============================================================================

export function useMissionTheme() {
  const { mode, ground } = useThemeMode();
  return useMemo(() => {
    const mc = missionColorsForGround(ground);
    return {
      mode,
      mc,
      pl: powerLineColorsForGround(ground),
      sla: slaColorsForGround(ground),
      glass: buildGlassStyle(mc),
      badges: buildIntelBadges(mc),
    };
  }, [mode, ground]);
}

// ============================================================================
// Static Exports (backward-compat — dark mode locked)
// ============================================================================

/** @deprecated Use useMissionTheme() for mode-aware colors */
export const missionColors = darkMissionColors;
/** @deprecated Use useMissionTheme() for mode-aware colors */
export const powerLineColors = darkPowerLineColors;
/** @deprecated Use useMissionTheme() for mode-aware colors */
export const intelBadges = buildIntelBadges(darkMissionColors);
/** @deprecated Use useMissionTheme() for mode-aware colors */
export const slaColors = darkSlaColors;
/** @deprecated Use useMissionTheme() for mode-aware colors */
export const glassStyle = buildGlassStyle(darkMissionColors);

// Animation keyframes (mode-independent)
export const animations = {
  breathe: `
    @keyframes breathe {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(0.95); }
    }
  `,
  pulse: `
    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(0, 212, 255, 0.4); }
      50% { box-shadow: 0 0 0 10px rgba(0, 212, 255, 0); }
    }
  `,
  glow: `
    @keyframes glow {
      0%, 100% { filter: brightness(1); }
      50% { filter: brightness(1.3); }
    }
  `,
  flowRight: `
    @keyframes flowRight {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `,
};
